import React from 'react';
import { useTranslation } from 'react-i18next';

import ConnectHomeShell, { getConnectHomeCopy } from '../Components/Blocks/ConnectHomeShell';
import WebAppzAPI from '../API/WebAppzAPI';
import '../Assets/Css/style.css';

const LazySelectLanguage = React.lazy(() => import('../Comps/Control/SelectLanguage'));
const LazyMyShops = React.lazy(() => import('../Components/Blocks/Shops'));
const LazyMyRefs = React.lazy(() => import('../Components/Blocks/Refs'));
const LazyWalletRow = React.lazy(() => import('../Components/TelegramWallet/LazyWalletRow'));

export default function WelcomePage({
    user,
    refs,
    shops,
    shopsLoaded,
    shopsLoading,
    loadShops,
    setOpenModalShops,
    openModalShops,
    ready = true,
}) {
    const { t } = useTranslation();
    const [openLanguage, setOpenLanguage] = React.useState(false);
    const [openRefs, setOpenRefs] = React.useState(false);
    const [openShop, setOpenShop] = React.useState(false);
    const [walletAction, setWalletAction] = React.useState(null);
    const [languageCode, setLanguageCode] = React.useState(user.language_code || 'en');

    React.useEffect(() => {
        setLanguageCode(user.language_code || 'en');
    }, [user.language_code]);

    const labels = getConnectHomeCopy(languageCode, t);
    const isRussian = String(languageCode).toLowerCase().startsWith('ru');
    const channelUsername = isRussian ? 'webappz' : 'webappzorg';
    const demoTelegramLink = isRussian
        ? 'https://t.me/webappzbot/demo'
        : 'https://t.me/webappzbot/demoen';

    const scanQrCode = async (token) => {
        await WebAppzAPI.checkQrCrmToken(token, user);
        return false;
    };

    const handleWalletClick = () => {
        if (user.WalletAddress) {
            const confirmed = window.confirm(t('confirm_disconnect_wallet'));
            if (!confirmed) {
                return;
            }
            setWalletAction({ type: 'disconnect', id: Date.now() });
            return;
        }

        setWalletAction({ type: 'connect', id: Date.now() });
    };

    const actions = {
        language: () => setOpenLanguage(true),
        qr: () => window.Telegram?.WebApp?.showScanQrPopup?.({}, scanQrCode),
        balance: () => {
            if (Number(user.balance) >= 1) {
                window.location.href = `https://t.me/appflap?text=${t('hello_withdraw_money')}`;
                return;
            }
            window.Telegram?.WebApp?.showAlert?.(t('min_one_ton'));
        },
        wallet: handleWalletClick,
        shops: () => {
            setOpenModalShops(true);
            loadShops?.().catch(() => {});
        },
        referrals: () => setOpenRefs(true),
        demo: () => window.Telegram?.WebApp?.openTelegramLink?.(demoTelegramLink),
        website: () => window.Telegram?.WebApp?.openLink?.('https://webappz.org/'),
        channel: () => window.Telegram?.WebApp?.openTelegramLink?.(`https://t.me/${channelUsername}`),
        help: () => {
            window.location.href = 'https://t.me/appflap';
        },
    };

    const safeRefs = refs || {
        firstLevel: user.firstLevelRefs || [],
        secondLevel: user.secondLevelRefs || [],
    };

    return (
        <>
            <ConnectHomeShell
                languageCode={languageCode}
                labels={labels}
                balance={user.balance}
                walletAddress={user.WalletAddress}
                ready={ready}
                actions={actions}
            />

            {ready && openLanguage && (
                <React.Suspense fallback={null}>
                    <LazySelectLanguage
                        user={{ ...user, language_code: languageCode }}
                        hideTrigger
                        open={openLanguage}
                        onOpenChange={setOpenLanguage}
                        onLanguageChange={setLanguageCode}
                    />
                </React.Suspense>
            )}

            {ready && openModalShops && (
                <React.Suspense fallback={null}>
                    <LazyMyShops
                        setOpenModalShops={setOpenModalShops}
                        openModalShops={openModalShops}
                        userData={user}
                        shops={shops}
                        shopsLoaded={shopsLoaded}
                        shopsLoading={shopsLoading}
                        loadShops={loadShops}
                        openShop={openShop}
                        setOpenShop={setOpenShop}
                        hideTrigger
                    />
                </React.Suspense>
            )}

            {ready && openRefs && (
                <React.Suspense fallback={null}>
                    <LazyMyRefs
                        firstLevel={safeRefs.firstLevel || []}
                        secondLevel={safeRefs.secondLevel || []}
                        hideTrigger
                        open={openRefs}
                        onOpenChange={setOpenRefs}
                    />
                </React.Suspense>
            )}

            {ready && walletAction && (
                <React.Suspense fallback={null}>
                    <LazyWalletRow
                        key={walletAction.id}
                        user={user}
                        requestedAction={walletAction.type}
                        headless
                    />
                </React.Suspense>
            )}
        </>
    );
}
